import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";

const fmt = (v, digits = 2) =>
  v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

export default function AdminIndices() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, with_nifty50: 0, with_smallcap50: 0 });
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [gapFrom, setGapFrom] = useState("");
  const [gapTo, setGapTo] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  // Manual entry form state
  const [manualDate, setManualDate] = useState(new Date().toISOString().slice(0, 10));
  const [manualN50, setManualN50] = useState("");
  const [manualSc50, setManualSc50] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await api.get("/admin/indices/list", { params });
      setRows(r.data.rows || []);
      setMeta({ total: r.data.total, with_nifty50: r.data.with_nifty50, with_smallcap50: r.data.with_smallcap50 });
    } catch (e) {
      toast.error(`Failed to load indices: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const fillGap = async () => {
    if (!gapFrom) {
      toast.error("Enter a from-date");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/indices/fill-gap", { from_date: gapFrom, to_date: gapTo });
      toast.success(`Backfilled: ${r.data.inserted} inserted, ${r.data.updated} updated across ${r.data.dates_covered} dates`);
      await load();
    } catch (e) {
      toast.error(`Fill-gap failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (trade_date) => {
    if (!window.confirm(`Delete indices row for ${trade_date}?`)) return;
    try {
      await api.delete(`/admin/indices/${trade_date}`);
      toast.success(`Deleted ${trade_date}`);
      setRows(rows.filter((r) => r.trade_date !== trade_date));
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const refreshToday = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/indices/refresh-yahoo", {});
      const today = r.data.results?.[0];
      toast.success(`Refreshed ${today?.trade_date}: N50=${fmt(today?.nifty50_close, 2)}, SC50=${fmt(today?.smallcap50_close, 2)}`);
      await load();
    } catch (e) {
      toast.error(`Refresh failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const saveManualEntry = async () => {
    if (!manualDate) {
      toast.error("Date required");
      return;
    }
    if (!manualN50 && !manualSc50) {
      toast.error("Enter at least one value (Nifty 50 or Smallcap 50)");
      return;
    }
    setBusy(true);
    try {
      const payload = { trade_date: manualDate };
      if (manualN50) payload.nifty50_close = parseFloat(manualN50);
      if (manualSc50) payload.nifty_smallcap50_close = parseFloat(manualSc50);
      const r = await api.post("/admin/indices/manual-entry", payload);
      toast.success(`Saved ${manualDate} — ${r.data.inserted ? "inserted" : "updated"}`);
      setManualN50("");
      setManualSc50("");
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const sourceBadge = (r) => {
    const src = r.source || "unknown";
    const cls =
      src === "bulk_csv" || src === "manual_test"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : src === "yahoo_backfill"
        ? "bg-blue-50 text-blue-700 border-blue-200"
        : src === "yahoo_autoseed"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-zinc-50 text-zinc-700 border-zinc-200";
    return (
      <Badge className={`${cls} text-[10px] uppercase tracking-wider`} data-testid={`src-${src}`}>
        {src}
      </Badge>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="admin-indices-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Admin</div>
          <h1 className="text-3xl font-display font-bold text-zinc-900">Indices Data</h1>
          <div className="text-sm text-zinc-500 mt-1">
            Historical Nifty 50 + Nifty Smallcap 50 closes used for portfolio benchmark returns.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="reload-btn">
            Reload
          </Button>
        </div>
      </div>

      {/* Coverage summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Total Rows</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-mono font-bold text-zinc-900" data-testid="meta-total">
              {meta.total.toLocaleString("en-IN")}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">With Nifty 50</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-mono font-bold text-emerald-700" data-testid="meta-n50">
              {meta.with_nifty50.toLocaleString("en-IN")}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">With Smallcap 50</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-mono font-bold text-emerald-700" data-testid="meta-sc50">
              {meta.with_smallcap50.toLocaleString("en-IN")}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Manual entry — primary flow. Bhavcopy CSV uploads also mirror here automatically. */}
      <Card className="border-purple-200">
        <CardHeader>
          <CardTitle className="text-base font-display font-semibold flex items-center gap-2">
            Manual Entry
            <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] uppercase tracking-wider">
              Recommended
            </Badge>
          </CardTitle>
          <div className="text-xs text-zinc-500 mt-1">
            Enter today&apos;s NSE-published Nifty 50 + Nifty Smallcap 50 close values. Overwrites existing rows for the same date.
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 flex-wrap items-end">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Date</label>
              <Input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} className="w-40" data-testid="manual-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Nifty 50 Close</label>
              <Input
                type="number"
                step="0.01"
                placeholder="23,456.78"
                value={manualN50}
                onChange={(e) => setManualN50(e.target.value)}
                className="w-44 font-mono"
                data-testid="manual-n50"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Smallcap 50 Close</label>
              <Input
                type="number"
                step="0.01"
                placeholder="18,234.56"
                value={manualSc50}
                onChange={(e) => setManualSc50(e.target.value)}
                className="w-44 font-mono"
                data-testid="manual-sc50"
              />
            </div>
            <Button onClick={saveManualEntry} disabled={busy || !manualDate} data-testid="manual-save-btn">
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Fill-gap panel — opt-in Yahoo fetch (kept as fallback but no longer auto-triggered) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display font-semibold flex items-center gap-2">
            Fill Gap from Yahoo
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] uppercase tracking-wider">
              Optional / Unreliable
            </Badge>
          </CardTitle>
          <div className="text-xs text-zinc-500 mt-1">
            Attempts to fetch Nifty 50 (^NSEI) and Smallcap 50 (NIFTYSMLCAP50.NS) from Yahoo Finance. Yahoo often withholds historical data for these tickers — use Manual Entry above as your primary source.
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 flex-wrap items-end">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">From Date</label>
              <Input type="date" value={gapFrom} onChange={(e) => setGapFrom(e.target.value)} className="w-40" data-testid="gap-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">To Date</label>
              <Input type="date" value={gapTo} onChange={(e) => setGapTo(e.target.value)} className="w-40" data-testid="gap-to" />
            </div>
            <Button onClick={fillGap} disabled={busy || !gapFrom} data-testid="fill-gap-btn">
              {busy ? "Backfilling…" : "Fill Gap"}
            </Button>
            <div className="text-xs text-zinc-500 max-w-md">
              Only fills empty dates. Never overwrites existing rows (manual bhavcopy uploads always win).
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter bar */}
      <div className="flex gap-3 items-end flex-wrap">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Filter From</label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="filter-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Filter To</label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" data-testid="filter-to" />
        </div>
        <Button size="sm" variant="ghost" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="clear-filter">
          Clear
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-right px-4 py-3">Nifty 50 Close</th>
                  <th className="text-right px-4 py-3">Smallcap 50 Close</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">N50 Ticker</th>
                  <th className="text-left px-4 py-3">SC50 Ticker</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-zinc-500">Loading…</td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-zinc-500" data-testid="empty-state">
                      No indices data yet. Upload a bhavcopy CSV or click &quot;Fill Gap&quot; above to fetch from Yahoo Finance.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.trade_date} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`idx-row-${r.trade_date}`}>
                    <td className="px-4 py-3 font-mono text-zinc-800">{fmtDate(r.trade_date)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(r.nifty50_close, 2)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(r.nifty_smallcap50_close, 2)}</td>
                    <td className="px-4 py-3">{sourceBadge(r)}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{r.nifty50_source_ticker || "—"}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{r.smallcap50_source_ticker || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => deleteRow(r.trade_date)}
                        className="text-rose-600 hover:text-rose-800 text-xs font-medium"
                        data-testid={`delete-${r.trade_date}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
